import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { httpPost, useHttpGets } from "../../../services/httpService";
import { ApiEndPoints } from "../../../utils/endpoints";
import { IWatchListData } from "../../../interface/Watchlist/propertyDetails";
import ViewPropertyDetails from "../../../interface/Watchlist/propertyDetails";
import PropertyHeader from "./PropertyHeader";
import PropertyInformation from "./PropertyInformation";
import { REIProgressSpinner } from "../../../components/ui";
import { RootState } from "../../../store";
import {
  setPropertyDetails,
  setPropertySummary,
  setShowFileUploadNotes,
} from "../../../store/slice/propertyDetailsSlice";
import { Mapper } from "../../ProppertyAnalyser/Property Summary/mapper";
import { MEMBER_KEY, USER_DETAIL } from "../../../utils/constants";
import { IAnalysisSummaryResponse } from "../../../interface/analysisSummary";

interface ViewPropertyProp {
  onClose?: () => void;
  propertyDetails?: IWatchListData;
  hideCloseBtn?: boolean;
  isFromPropertyAnalyser?: boolean;
  propertyAddress?: string;
}

const PropertyDetails: React.FC<ViewPropertyProp> = ({
  onClose,
  propertyDetails,
  hideCloseBtn,
  isFromPropertyAnalyser = false,
  propertyAddress,
}) => {
  const [propertyData, setPropertyData] = useState<ViewPropertyDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [refreshData, setRefreshData] = useState<boolean>(false);
  const analysisSummary = useSelector((state: RootState) => state.analyser.analysisSummary as IAnalysisSummaryResponse);
  const dispatch = useDispatch();
  const hasFetched = useRef<string | null>(null);
  const mappedData = useRef<ViewPropertyDetails>();
  const memberDetail = JSON.parse(localStorage.getItem(USER_DETAIL) || "{}");
  const memberId = localStorage.getItem(MEMBER_KEY);

  useEffect(() => {
    if (!propertyDetails?.PropertyID && !isFromPropertyAnalyser) {
      return;
    }

    const fetchPropertySummary = () => {
      if (!analysisSummary?.PropertyAnalyserId) {
        return;
      }
      setLoading(true);
      useHttpGets(
        `${ApiEndPoints.analyser}/PropertyDetailSummary?MemberId=${memberId}&PropertyAnalyserId=${analysisSummary.PropertyAnalyserId}&MemberCountryCode=${memberDetail.Country}`,
        undefined
      )
        .then((result) => {
          if (result.status === 200) {
            mappedData.current = Mapper(result.data, propertyAddress || "");
            dispatch(setPropertySummary(mappedData.current));
            dispatch(setShowFileUploadNotes(true));
            if (JSON.stringify(propertyData) !== JSON.stringify(mappedData.current)) {
              setPropertyData(mappedData.current);
            }
          }
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    };

    const fetchData = async () => {
      try {
        setLoading(true);

        const parameters = {
          CountryCode: propertyDetails?.CountryID,
          PropertyId: propertyDetails?.PropertyID,
          LinkIdHash: propertyDetails?.LinkIDHash,
          MemberId: propertyDetails?.MemberId,
        };

        const result = await httpPost(`${ApiEndPoints.searchSummary}/AU/Search/PropertyDetails/`, parameters);

        if (result.status === 200 && result.data && result.data.Result?.length > 0) {
          const updatedData = {
            ...result.data?.Result[0],
            PropertyDetails: {
              ...result.data?.Result[0].PropertyDetails,
              WatchlistID: propertyDetails?.WatchlistID,
              MemberId: propertyDetails?.MemberId,
            },
            isFromPropertyAnalyser: false,
          };

          if (JSON.stringify(propertyData) !== JSON.stringify(updatedData)) {
            setPropertyData(updatedData);
            dispatch(setPropertyDetails(updatedData));
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
        hasFetched.current = propertyDetails.PropertyID;
      }
    };

    isFromPropertyAnalyser ? fetchPropertySummary() : fetchData();
  }, [propertyDetails?.PropertyID, refreshData, analysisSummary]);

  // Memoize the callback to avoid unnecessary re-renders
  const handleCallback = useCallback(() => {
    onClose?.();
  }, [onClose]);

  // Memoize propertyData to prevent unnecessary re-renders of PropertyInformation
  const memoizedPropertyData = useMemo(() => propertyData, [propertyData, refreshData]);

  return (
    <>
      {loading && (
        <div className="w-full flex items-center min-h-60">
          <REIProgressSpinner />
        </div>
      )}
      {!loading && propertyData && (
        <>
          <PropertyHeader
            propertydata={memoizedPropertyData}
            onClosecallback={handleCallback}
            hideCloseBtn={hideCloseBtn}
          />
          <PropertyInformation viewPropertyData={memoizedPropertyData} />
        </>
      )}
    </>
  );
};

export { PropertyDetails };
